// @ts-check
/**
 * Minimal DXLink (dxFeed WebSocket) client for tastytrade candles: history snapshots and live updates.
 * Protocol: https://developer.tastytrade.com/docs/guides/stream-market-data/
 */
(function tastytradeDxLinkClosure() {
  'use strict';

  const DXLINK_CHANNEL = 3;
  const KEEPALIVE_MS = 30 * 1000;
  const MAX_RECONNECT_DELAY_MS = 30 * 1000;
  // History requests normally end with a SNAPSHOT_END row; this caps the wait when one never comes.
  const SNAPSHOT_TIMEOUT_MS = 15 * 1000;
  // Candle eventFlags bits.
  const REMOVE_EVENT = 2;
  const SNAPSHOT_END = 8;
  const CANDLE_FIELDS = ['eventType', 'eventSymbol', 'time', 'eventFlags', 'open', 'high', 'low', 'close', 'volume'];
  // Day summary for watchlist cards: Quote = bid/ask, Trade = last, Summary = day open/high/low and previous close.
  const QUOTE_EVENTS = ['Quote', 'Trade', 'Summary'];
  const EVENT_FIELDS = {
    Candle: CANDLE_FIELDS,
    Quote: ['eventType', 'eventSymbol', 'bidPrice', 'askPrice'],
    Trade: ['eventType', 'eventSymbol', 'price', 'time'],
    Summary: ['eventType', 'eventSymbol', 'dayOpenPrice', 'dayHighPrice', 'dayLowPrice', 'prevDayClosePrice'],
  };
  // How long a first price request waits for the stream before answering with what it has.
  const QUOTE_TIMEOUT_MS = 8 * 1000;
  // Quote, Trade and Summary arrive separately; after the first price, wait this long for the rest.
  const QUOTE_SETTLE_MS = 1500;
  // Watched prices nobody asked for this long are unsubscribed, checked at most once a minute.
  const QUOTE_IDLE_MS = 5 * 60 * 1000;
  const QUOTE_PRUNE_MS = 60 * 1000;
  const CLOSED = 'tastytrade market data connection closed';

  /**
   * @typedef {Object} DxQuote - latest values for one symbol; NaN until the feed sends them
   * @property {number} bid
   * @property {number} ask
   * @property {number} last
   * @property {number} open - today
   * @property {number} high - today
   * @property {number} low - today
   * @property {number} prevClose
   * @property {number} time - epoch ms of the last update
   */

  /**
   * @typedef {Object} DxCandle
   * @property {number} time - candle start, epoch ms
   * @property {number} open
   * @property {number} high
   * @property {number} low
   * @property {number} close
   * @property {number} volume
   */

  class TastytradeDxLink {
    /**
     * @param {(isRefused: boolean) => PromiseLike<{ token: string, url: string }>} getToken - quote token for the
     *   account's environment; `isRefused` asks for a new one instead of the cached one
     */
    constructor(getToken) {
      this.getToken = getToken;
      /** @type {WebSocket | null} */
      this.socket = null;
      /** @type {Promise<void> | null} */
      this.ready = null;
      // True once the feed channel is open; until then resubscribe() sends what was requested meanwhile.
      this.isOpen = false;
      /** @type {Record<string, string[]>} COMPACT field order per event type */
      this.fields = Object.assign({}, EVENT_FIELDS);
      /** @type {Record<string, Set<(candles: DxCandle[], isSnapshotEnd: boolean) => void>>} */
      this.listeners = {};
      /** @type {Record<string, number>} candle symbol -> fromTime, resent after a reconnect */
      this.subscriptions = {};
      /** @type {Record<string, number>} candle symbol -> fromTime of its live chart stream */
      this.liveSince = {};
      /** @type {Record<string, Promise<*>>} last snapshot request per candle symbol; the next one waits for it */
      this.historyQueue = {};
      /** @type {Set<(error?: Error) => void>} history requests still waiting for their snapshot */
      this.pending = new Set();
      /** @type {Record<string, DxQuote>} symbols watched for prices, until idle (pruneQuotes) or close() */
      this.quotes = {};
      /** @type {Record<string, Set<(error?: Error) => void>>} first-price waiters per symbol */
      this.quoteWaiters = {};
      /** @type {Record<string, Promise<DxQuote>>} the one wait per symbol that later polls share */
      this.quotePending = {};
      /** @type {Record<string, number>} epoch ms each watched symbol was last asked for */
      this.quoteReads = {};
      this.lastPrune = 0;
      /** @type {Record<string, *>} settle timers of symbols that got a first price */
      this.settleTimers = {};
      /** @type {*} */
      this.reconnectTimer = null;
      this.reconnectAttempts = 0;
      this.isTokenRefused = false;
      // Bumped by close(), so an open() still waiting for its token knows it was cancelled.
      this.generation = 0;
    }

    /**
     * Candle history from `fromTime` to now.
     *
     * @param {string} symbol - tastytrade streamer symbol, e.g. `AAPL`
     * @param {string} period - dxFeed period, e.g. `5m`, `h`, `d`
     * @param {number} fromTime - epoch ms
     * @return {Promise<DxCandle[]>} sorted by time
     */
    getCandles(symbol, period, fromTime) {
      const key = toCandleSymbol(symbol, period);
      return this.enqueueHistory(key, () => this.requestCandles(key, fromTime, false));
    }

    /**
     * Listeners are per candle symbol, so two snapshots in flight would each end the other's request early.
     *
     * @param {string} key - candle symbol
     * @param {() => Promise<T>} request
     * @return {Promise<T>}
     * @template T
     */
    enqueueHistory(key, request) {
      const previous = this.historyQueue[key] || Promise.resolve();
      // close() empties the queue, but entries already chained still run; they must not reopen a discarded link.
      const generation = this.generation;
      const next = previous
        .then(
          () => undefined,
          () => undefined
        )
        .then(() => (generation === this.generation ? request() : Promise.reject(new Error(CLOSED))));
      this.historyQueue[key] = next;
      const clear = () => {
        if (this.historyQueue[key] === next) delete this.historyQueue[key];
      };
      next.then(clear, clear);
      return next;
    }

    /**
     * Subscribes from `fromTime` and collects the snapshot up to its SNAPSHOT_END.
     *
     * @param {string} key - candle symbol
     * @param {number} fromTime - epoch ms
     * @param {boolean} isEmptyOk - a live stream may have nothing yet; history with nothing at all has stalled
     * @return {Promise<DxCandle[]>} sorted by time
     */
    requestCandles(key, fromTime, isEmptyOk) {
      /** @type {Record<string, DxCandle>} */
      const byTime = {};

      return new Promise((resolve, reject) => {
        /** @type {*} */
        let timer = null;
        const listener = (candles, isSnapshotEnd) => {
          candles.forEach((candle) => {
            if (candle.time >= fromTime) byTime[candle.time] = candle;
          });
          if (isSnapshotEnd) finish();
        };
        // Stops listening; the stream itself stays while a live chart still uses the symbol.
        const finish = (error) => {
          clearTimeout(timer);
          this.pending.delete(finish);
          this.removeListener(key, listener);
          // A one-off history request must not move the live stream's start back for later reconnects.
          if (this.subscriptions[key] !== undefined && this.liveSince[key] !== undefined) {
            this.subscriptions[key] = this.liveSince[key];
          }
          if (error) reject(error);
          else resolve();
        };

        // Nothing at all by then is a stalled feed, not an empty range: an error, so charts do not show "no data".
        timer = setTimeout(() => {
          finish(
            Object.keys(byTime).length || isEmptyOk ? undefined : new Error('tastytrade candle history timed out')
          );
        }, SNAPSHOT_TIMEOUT_MS);
        this.pending.add(finish);
        this.addListener(key, listener);
        this.subscribe(key, fromTime).catch(finish);
      }).then(() =>
        Object.keys(byTime)
          .map((time) => byTime[time])
          .sort((a, b) => a.time - b.time)
      );
    }

    /**
     * Live updates of the current candle (and new ones) for a chart.
     *
     * @param {string} symbol - tastytrade streamer symbol
     * @param {string} period - dxFeed period
     * @param {(candle: DxCandle) => void} onCandle
     * @return {Promise<() => void>} stop function
     */
    subscribeCandles(symbol, period, onCandle) {
      const key = toCandleSymbol(symbol, period);
      // Wide enough to include the current candle even on the delayed demo feed.
      const since = Date.now() - Math.max(24 * 60 * 60 * 1000, 3 * getPeriodMs(period));
      let latest = 0;
      // The subscription first replays history (newest first); a chart accepts only its current or a newer bar.
      const listener = (candles) => {
        const sortedCandles = candles.slice().sort((a, b) => a.time - b.time);
        if (!latest && sortedCandles.length) latest = sortedCandles[sortedCandles.length - 1].time;
        sortedCandles.forEach((candle) => {
          if (candle.time < latest) return;
          latest = candle.time;
          onCandle(candle);
        });
      };

      const isStreaming = this.liveSince[key] !== undefined;
      this.liveSince[key] = Math.min(since, this.liveSince[key] || Infinity);
      this.addListener(key, listener);
      const stop = () => this.removeListener(key, listener);
      // Another chart already streams this symbol: a later fromTime may bring no new snapshot to wait for.
      if (isStreaming) return Promise.resolve(stop);
      // Queued with history requests (its snapshot could end theirs); on failure the listener must not outlive it.
      return this.enqueueHistory(key, () => this.requestCandles(key, since, true)).then(
        () => stop,
        (error) => {
          stop();
          throw error;
        }
      );
    }

    /**
     * Latest prices for a symbol, streamed from then on. The first call waits for the first data.
     *
     * @param {string} symbol - tastytrade streamer symbol
     * @return {Promise<DxQuote>}
     */
    watchQuote(symbol) {
      this.quoteReads[symbol] = Date.now();
      this.pruneQuotes(false);
      const quote = this.quotes[symbol];
      const isWaiting = !!(this.quoteWaiters[symbol] && this.quoteWaiters[symbol].size);
      if (quote && (checkQuoteComplete(quote) || (checkHasPrice(quote) && !isWaiting))) return Promise.resolve(quote);
      // Each repeated poll would otherwise add another waiter and timer for a symbol that has not priced yet.
      if (this.quotePending[symbol]) return this.quotePending[symbol];
      if (!quote) this.quotes[symbol] = this.emptyQuote();

      const pending = new Promise((resolve, reject) => {
        /** @type {*} */
        let timer = null;
        const finish = (error) => {
          clearTimeout(timer);
          if (this.quoteWaiters[symbol]) this.quoteWaiters[symbol].delete(finish);
          if (error) reject(error);
          else resolve(this.quotes[symbol] || this.emptyQuote());
        };

        // Nothing at all by then becomes a gap in the batch, not a NaN price on the card.
        timer = setTimeout(() => {
          const isPriced = checkHasPrice(this.quotes[symbol] || this.emptyQuote());
          finish(isPriced ? undefined : new Error('No tastytrade price for ' + symbol));
        }, QUOTE_TIMEOUT_MS);
        (this.quoteWaiters[symbol] = this.quoteWaiters[symbol] || new Set()).add(finish);

        if (!quote) {
          const isOpen = this.isOpen;
          this.connect()
            .then(() => {
              if (isOpen) {
                this.send({ type: 'FEED_SUBSCRIPTION', channel: DXLINK_CHANNEL, add: toQuoteSubscriptions([symbol]) });
              }
            })
            .catch((error) => {
              // Forget it so the next request subscribes again.
              delete this.quotes[symbol];
              finish(error);
            });
        }
      });

      this.quotePending[symbol] = pending;
      const clear = () => {
        if (this.quotePending[symbol] === pending) delete this.quotePending[symbol];
      };
      pending.then(clear, clear);
      return pending;
    }

    /**
     * Stops streaming prices nobody asked for in QUOTE_IDLE_MS, e.g. stocks removed from the watchlist.
     *
     * @param {boolean} isForced - skip the once-a-minute throttle
     */
    pruneQuotes(isForced) {
      const now = Date.now();
      if (!isForced && now - this.lastPrune < QUOTE_PRUNE_MS) return;
      this.lastPrune = now;

      const idleSymbols = Object.keys(this.quotes).filter(
        (symbol) => !this.quotePending[symbol] && now - (this.quoteReads[symbol] || 0) > QUOTE_IDLE_MS
      );
      if (!idleSymbols.length) return;

      idleSymbols.forEach((symbol) => {
        clearTimeout(this.settleTimers[symbol]);
        delete this.settleTimers[symbol];
        delete this.quotes[symbol];
        delete this.quoteReads[symbol];
        delete this.quoteWaiters[symbol];
      });
      this.send({ type: 'FEED_SUBSCRIPTION', channel: DXLINK_CHANNEL, remove: toQuoteSubscriptions(idleSymbols) });
    }

    /**
     * @return {DxQuote}
     */
    emptyQuote() {
      return { bid: NaN, ask: NaN, last: NaN, open: NaN, high: NaN, low: NaN, prevClose: NaN, time: 0 };
    }

    /**
     * Closes the socket; the next request opens a new one.
     */
    close() {
      const socket = this.socket;
      this.generation++;
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
      this.listeners = {};
      this.subscriptions = {};
      this.liveSince = {};
      this.historyQueue = {};
      this.quotes = {};
      this.quotePending = {};
      this.quoteReads = {};
      this.socket = null;
      this.ready = null;
      this.isOpen = false;
      Object.keys(this.settleTimers).forEach((symbol) => clearTimeout(this.settleTimers[symbol]));
      this.settleTimers = {};
      this.failPending(new Error(CLOSED));
      Object.keys(this.quoteWaiters).forEach((symbol) =>
        this.quoteWaiters[symbol].forEach((finish) => finish(new Error(CLOSED)))
      );
      if (socket) socket.close();
    }

    /**
     * @param {Error} error
     */
    failPending(error) {
      this.pending.forEach((finish) => finish(error));
    }

    /**
     * @return {boolean} whether live charts or watched prices still need the stream
     */
    checkStreaming() {
      return Object.keys(this.subscriptions).length > 0 || Object.keys(this.quotes).length > 0;
    }

    /**
     * Reopens a dropped connection while live charts or prices still need it: 1s, 2s, 4s ... up to 30s.
     */
    scheduleReconnect() {
      if (this.reconnectTimer) return;
      const delay = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * Math.pow(2, this.reconnectAttempts++));

      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        // Without this, prices nobody watches any more would keep the reconnect loop going forever.
        this.pruneQuotes(true);
        if (!this.checkStreaming()) return;
        this.connect().then(
          () => {
            this.reconnectAttempts = 0;
          },
          (error) => {
            console.warn('tastytrade market data reconnect failed (attempt ' + this.reconnectAttempts + ')', error);
            this.scheduleReconnect();
          }
        );
      }, delay);
    }

    /**
     * @param {string} key - candle symbol
     * @param {(candles: DxCandle[], isSnapshotEnd: boolean) => void} listener
     */
    addListener(key, listener) {
      (this.listeners[key] = this.listeners[key] || new Set()).add(listener);
    }

    /**
     * Drops the DXLink subscription once nobody listens to the symbol any more.
     *
     * @param {string} key - candle symbol
     * @param {Function} listener
     */
    removeListener(key, listener) {
      const set = this.listeners[key];
      if (!set) return;
      set.delete(/** @type {*} */ (listener));
      if (set.size) return;

      delete this.listeners[key];
      delete this.subscriptions[key];
      delete this.liveSince[key];
      this.send({ type: 'FEED_SUBSCRIPTION', channel: DXLINK_CHANNEL, remove: [{ type: 'Candle', symbol: key }] });
    }

    /**
     * @param {string} key - candle symbol
     * @param {number} fromTime - epoch ms; resubscribing with an earlier one sends a new history snapshot
     * @return {Promise<void>}
     */
    subscribe(key, fromTime) {
      this.subscriptions[key] = Math.min(fromTime, this.subscriptions[key] || Infinity);
      const isOpen = this.isOpen;
      return this.connect().then(() => {
        if (!isOpen) return;
        this.send({
          type: 'FEED_SUBSCRIPTION',
          channel: DXLINK_CHANNEL,
          add: [{ type: 'Candle', symbol: key, fromTime: fromTime }],
        });
      });
    }

    /**
     * @return {Promise<void>} resolves when the feed channel is open
     */
    connect() {
      if (!this.ready) {
        const ready = this.open().catch((error) => {
          // After a close() a newer connect() may own `ready`; clearing that one would open a third socket.
          if (this.ready === ready) this.ready = null;
          throw error;
        });
        this.ready = ready;
      }
      return this.ready;
    }

    /**
     * @return {Promise<void>}
     */
    open() {
      const generation = this.generation;
      return Promise.resolve(this.getToken(this.isTokenRefused)).then((access) => {
        if (generation !== this.generation) throw new Error(CLOSED);
        return this.openSocket(access);
      });
    }

    /**
     * @param {{ token: string, url: string }} access - quote token and DXLink URL
     * @return {Promise<void>}
     */
    openSocket(access) {
      return new Promise((resolve, reject) => {
        const socket = new WebSocket(access.url);
        // Per socket, so a stale socket closing late cannot touch the current one.
        let isAuthSent = false;
        /** @type {*} */
        let keepalive = null;
        this.socket = socket;

        socket.onopen = () => {
          this.send({
            type: 'SETUP',
            channel: 0,
            version: '0.1-DXF-JS/0.3.0',
            keepaliveTimeout: 60,
            acceptKeepaliveTimeout: 60,
          });
          keepalive = setInterval(() => this.send({ type: 'KEEPALIVE', channel: 0 }), KEEPALIVE_MS);
        };

        /** @param {*} message */
        const handleMessage = (message) => {
          if (message.type === 'AUTH_STATE' && message.state === 'UNAUTHORIZED') {
            // The server says UNAUTHORIZED once before AUTH; a second time means the token was refused.
            if (isAuthSent) {
              this.isTokenRefused = true;
              reject(new Error('tastytrade market data token was refused'));
              socket.close();
              return;
            }
            isAuthSent = true;
            this.send({ type: 'AUTH', channel: 0, token: access.token });
          } else if (message.type === 'AUTH_STATE' && message.state === 'AUTHORIZED') {
            this.isTokenRefused = false;
            this.send({
              type: 'CHANNEL_REQUEST',
              channel: DXLINK_CHANNEL,
              service: 'FEED',
              parameters: { contract: 'AUTO' },
            });
          } else if (message.type === 'CHANNEL_OPENED' && message.channel === DXLINK_CHANNEL) {
            this.send({
              type: 'FEED_SETUP',
              channel: DXLINK_CHANNEL,
              acceptAggregationPeriod: 0.1,
              acceptDataFormat: 'COMPACT',
              acceptEventFields: EVENT_FIELDS,
            });
            this.isOpen = true;
            this.resubscribe();
            resolve();
          } else if (message.type === 'FEED_CONFIG' && message.eventFields) {
            // COMPACT rows follow the server's field order, which may differ from the one requested.
            Object.assign(this.fields, message.eventFields);
          } else if (message.type === 'FEED_DATA') {
            this.dispatch(message.data);
          } else if (message.type === 'ERROR') {
            console.error('tastytrade DXLink', message.error, message.message);
          }
        };
        // A malformed frame or a throwing chart listener must not break the socket handler.
        socket.onmessage = (event) => {
          try {
            handleMessage(JSON.parse(event.data));
          } catch (error) {
            console.error('tastytrade DXLink message failed', error);
          }
        };

        socket.onerror = () => reject(new Error('tastytrade market data connection failed'));
        socket.onclose = () => {
          clearInterval(keepalive);
          // No-op once open; settles a connect() that close() cut off mid-handshake.
          reject(new Error(CLOSED));
          if (this.socket !== socket) return;

          this.socket = null;
          this.ready = null;
          this.isOpen = false;
          this.failPending(new Error(CLOSED));
          if (this.checkStreaming()) this.scheduleReconnect();
        };
      });
    }

    /**
     * Sends the subscriptions and quotes requested before the channel opened, or lost with the previous socket.
     */
    resubscribe() {
      /** @type {Array<{ type: string, symbol: string, fromTime?: number }>} */
      const candles = Object.keys(this.subscriptions).map((key) => ({
        type: 'Candle',
        symbol: key,
        fromTime: this.subscriptions[key],
      }));
      const subscriptionsToAdd = candles.concat(toQuoteSubscriptions(Object.keys(this.quotes)));
      if (subscriptionsToAdd.length) {
        this.send({ type: 'FEED_SUBSCRIPTION', channel: DXLINK_CHANNEL, add: subscriptionsToAdd });
      }
    }

    /**
     * @param {Array<*>} data - COMPACT feed data: `['Candle', [v1, v2, ..., v1, v2, ...]]`
     */
    dispatch(data) {
      for (let i = 0; i < data.length; i += 2) {
        if (QUOTE_EVENTS.indexOf(data[i]) !== -1) this.dispatchQuotes(data[i], data[i + 1]);
      }

      const fields = this.fields.Candle;
      const index = (name) => fields.indexOf(name);
      /** @type {Record<string, { candles: DxCandle[], isSnapshotEnd: boolean }>} */
      const bySymbol = {};

      for (let i = 0; i < data.length; i += 2) {
        if (data[i] !== 'Candle') continue;
        const values = data[i + 1];

        for (let j = 0; j < values.length; j += fields.length) {
          const row = values.slice(j, j + fields.length);
          const key = normalizeCandleSymbol(row[index('eventSymbol')]);
          const flags = Number(row[index('eventFlags')]) || 0;
          const entry = (bySymbol[key] = bySymbol[key] || { candles: [], isSnapshotEnd: false });
          if (flags & SNAPSHOT_END) entry.isSnapshotEnd = true;

          const candle = {
            time: Number(row[index('time')]),
            open: Number(row[index('open')]),
            high: Number(row[index('high')]),
            low: Number(row[index('low')]),
            close: Number(row[index('close')]),
            volume: Number(row[index('volume')]) || 0,
          };
          // Snapshot markers and removed candles carry NaN prices.
          if (!(flags & REMOVE_EVENT) && candle.time > 0 && isFinite(candle.open) && isFinite(candle.close)) {
            entry.candles.push(candle);
          }
        }
      }

      Object.keys(bySymbol).forEach((key) => {
        (this.listeners[key] || new Set()).forEach((listener) => {
          listener(bySymbol[key].candles, bySymbol[key].isSnapshotEnd);
        });
      });
    }

    /**
     * @param {string} type - `Quote`, `Trade` or `Summary`
     * @param {Array<*>} values - COMPACT values of that type
     */
    dispatchQuotes(type, values) {
      const fields = this.fields[type];
      const value = (row, name) => Number(row[fields.indexOf(name)]);

      for (let j = 0; j < values.length; j += fields.length) {
        const row = values.slice(j, j + fields.length);
        const symbol = row[fields.indexOf('eventSymbol')];
        const quote = this.quotes[symbol];
        if (!quote) continue;

        if (type === 'Quote') {
          setIfFinite(quote, 'bid', value(row, 'bidPrice'));
          setIfFinite(quote, 'ask', value(row, 'askPrice'));
        } else if (type === 'Trade') {
          setIfFinite(quote, 'last', value(row, 'price'));
        } else {
          setIfFinite(quote, 'open', value(row, 'dayOpenPrice'));
          setIfFinite(quote, 'high', value(row, 'dayHighPrice'));
          setIfFinite(quote, 'low', value(row, 'dayLowPrice'));
          setIfFinite(quote, 'prevClose', value(row, 'prevDayClosePrice'));
        }
        quote.time = Date.now();
        this.releaseQuoteWaiters(symbol);
      }
    }

    /**
     * Answers first-price requests once the quote is complete, or shortly after its first price.
     *
     * @param {string} symbol
     */
    releaseQuoteWaiters(symbol) {
      const quote = this.quotes[symbol];
      const waiters = this.quoteWaiters[symbol];
      if (!quote || !waiters || !waiters.size || !checkHasPrice(quote)) return;

      const release = () => {
        clearTimeout(this.settleTimers[symbol]);
        delete this.settleTimers[symbol];
        if (this.quoteWaiters[symbol]) this.quoteWaiters[symbol].forEach((finish) => finish());
      };

      if (checkQuoteComplete(quote)) release();
      else if (!this.settleTimers[symbol]) this.settleTimers[symbol] = setTimeout(release, QUOTE_SETTLE_MS);
    }

    /**
     * @param {Object} message
     */
    send(message) {
      if (this.socket && this.socket.readyState === 1) this.socket.send(JSON.stringify(message));
    }
  }

  /**
   * @param {string[]} symbols - streamer symbols
   * @return {Array<{ type: string, symbol: string }>}
   */
  function toQuoteSubscriptions(symbols) {
    return symbols.reduce(
      (all, symbol) => all.concat(QUOTE_EVENTS.map((type) => ({ type: type, symbol: symbol }))),
      []
    );
  }

  /**
   * @param {DxQuote} quote
   * @return {boolean} whether there is any price to show yet
   */
  function checkHasPrice(quote) {
    return isFinite(quote.last) || isFinite(quote.bid) || isFinite(quote.ask);
  }

  /**
   * @param {DxQuote} quote
   * @return {boolean} whether a card can show it fully: a price and the previous close for the change
   */
  function checkQuoteComplete(quote) {
    return isFinite(quote.last) && isFinite(quote.prevClose);
  }

  /**
   * dxFeed sends NaN for values it does not have yet (e.g. day high before the open); keep the last known one.
   *
   * @param {DxQuote} quote
   * @param {string} key
   * @param {number} value
   */
  function setIfFinite(quote, key, value) {
    if (isFinite(value)) quote[key] = value;
  }

  /**
   * @param {string} symbol - streamer symbol
   * @param {string} period - dxFeed period
   * @return {string} e.g. `AAPL{=5m}`
   */
  function toCandleSymbol(symbol, period) {
    return normalizeCandleSymbol(symbol + '{=' + period + '}');
  }

  /**
   * @param {string} period - dxFeed period: `m`, `5m`, `h`, `4h`, `d`, `w`, `mo`
   * @return {number} approximate length in ms
   */
  function getPeriodMs(period) {
    const match = /^(\d*)(mo|m|h|d|w)$/.exec(period) || [];
    const units = { m: 60e3, h: 3600e3, d: 864e5, w: 7 * 864e5, mo: 31 * 864e5 };
    return (Number(match[1]) || 1) * (units[match[2]] || 864e5);
  }

  /**
   * dxFeed answers `AAPL{=1d}` as `AAPL{=d}`; both sides use the short form so they match.
   *
   * @param {string} symbol - candle symbol
   * @return {string}
   */
  function normalizeCandleSymbol(symbol) {
    return String(symbol).replace(/\{=1([a-z]+)\}/, '{=$1}');
  }

  window.TastytradeDxLink = TastytradeDxLink;
})();
